import React from 'react';
import { ModalViewButtons } from '@cp/ds/src/components/modalView';
import { Stack } from '@cp/ds/src/components/stack';
import { Button, ButtonProps } from '@cp/ds/src/components/button';
import { useIsMobile } from '@cp/ds/src/hooks/useIsMobile';
import { Container } from '@cp/ds/src/components/container';
import { Box } from '@cp/ds/src/components/box';
import { SxProps } from '@cp/ds';
import { FormField } from '@cp/ds/src/components/form';
import { AddressSearchFormField } from '@cp/entities/location';
import {
  CAREER_IDS_WITH_AFTER_YH_SCHOOL_PARAMETER,
  CAREER_IDS_WITH_AUTO_SELECTION_PARAMETER,
  CAREER_IDS_WITH_CHILDREN_PARAMETER,
  CAREER_IDS_WITH_CHILD_WITH_DISABILITIES_PARAMETER,
  CAREER_IDS_WITH_ENDED_AT_PARAMETER,
  CAREER_IDS_WITH_SICK_CHILD_PARAMETER,
  CAREER_IDS_WITH_STARTED_AT_PARAMETER,
} from '@common/const/catalog/item';
import { SubmitHandler, useFormContext } from 'react-hook-form';
import { ContentSkeleton } from '@cp/ds/src/components/contentSkeleton';
import { coreCareerApi } from '@cp/entities/career';
import { SerializedError } from '@reduxjs/toolkit';
import { ApiServerError } from '@cp/utils/rtk/query/model';
import { useApiServerErrorHandler } from 'src/shared/apiServer';
import { useTrackEvent } from 'src/shared/tracking';
import { ADDRESS_FIELD_NAME, CITY_FIELD_NAME, FormModel } from '../../model';
import { Time } from './time';
import { Children } from './children';
import { AutoSelection } from './autoSelection';

export interface VacancyFormProps {
  contentContainerSx?: SxProps;
  submitButtonTitle?: string;
  submitButtonProps?: Partial<ButtonProps>;
  secondaryButtonTitle?: string;
  secondaryButtonProps?: Partial<ButtonProps>;
  buttonsInsideFormContainer?: boolean;
  secondaryButtonIsAlwaysVisible?: boolean;
  onSubmit: (values: Omit<FormModel, 'address'> & { address: string; location: string }) => Promise<
    | {
        data: void;
      }
    | {
        error: ApiServerError | SerializedError;
      }
  >;
  onComplete: () => void;
  onSecondaryButtonClick?: () => void;
}

export const VacancyForm: React.FC<VacancyFormProps> = ({
  contentContainerSx,
  submitButtonTitle = 'Опубликовать',
  submitButtonProps,
  secondaryButtonTitle,
  secondaryButtonIsAlwaysVisible,
  secondaryButtonProps,
  buttonsInsideFormContainer,
  onSubmit,
  onComplete,
  onSecondaryButtonClick,
}) => {
  const form = useFormContext<FormModel>();
  const careerId = form.getValues('careerId');
  const isMobile = useIsMobile();
  const handleApiServerError = useApiServerErrorHandler();
  const track = useTrackEvent();

  const { data: careerDictionary, isLoading: isLoadingCareerDict } = coreCareerApi.endpoints.getCareers.useQuery();

  const handleSubmit = React.useCallback<SubmitHandler<FormModel>>(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async ({ date, address, ...values }) => {
      const response = await onSubmit({ ...values, address: address?.address as string, location: address?.location as string });

      if ('error' in response) {
        handleApiServerError(response.error, { form });
      } else {
        onComplete();
      }
    },
    [form, handleApiServerError, onComplete, onSubmit],
  );

  if (isLoadingCareerDict) {
    return <ContentSkeleton blocks={2} />;
  }

  if (!careerDictionary) {
    return null;
  }

  const buttons = (
    <Stack direction="row" gap={2} sx={{ alignItems: 'center' }}>
      <Button
        color="gray"
        sx={isMobile && !secondaryButtonIsAlwaysVisible ? { width: '100%' } : undefined}
        type="submit"
        variant="primary"
        {...submitButtonProps}
      >
        {submitButtonTitle}
      </Button>
      {secondaryButtonTitle && onSecondaryButtonClick && (secondaryButtonIsAlwaysVisible || !isMobile) ? (
        <Button color="pink" onClick={onSecondaryButtonClick} variant="primary" {...secondaryButtonProps}>
          {secondaryButtonTitle}
        </Button>
      ) : null}
    </Stack>
  );

  const hasDateTimeFields =
    CAREER_IDS_WITH_STARTED_AT_PARAMETER.includes(careerId) && CAREER_IDS_WITH_ENDED_AT_PARAMETER.includes(careerId);

  const Wrapper = isMobile ? Container : Box;
  const wrapperSx: SxProps | undefined = contentContainerSx;

  return (
    <Stack
      component="form"
      direction="column"
      noValidate
      onSubmit={form.handleSubmit(handleSubmit)}
      sx={{ pb: buttonsInsideFormContainer ? 0 : 3.5 }}
    >
      <Wrapper sx={wrapperSx}>
        <Stack direction="column" gap={2.5}>
          {!hasDateTimeFields && (
            <FormField
              fieldLabel="Короткое название задачи"
              name="translations.0.title"
              placeholder="Введите текст"
              sx={{ width: '100%' }}
              type="text"
            />
          )}

          {hasDateTimeFields && (
            <FormField
              disablePast
              fieldLabel="Дата"
              name="date"
              pickerType="date"
              placeholder="__.__.____"
              sx={{ width: 1, maxWidth: 1 }}
              type="dateTimePicker"
            />
          )}

          {hasDateTimeFields && <Time />}

          <AddressSearchFormField cityName={CITY_FIELD_NAME} fieldLabel="Адрес" name={ADDRESS_FIELD_NAME} placeholder="Заполните адрес" />

          {CAREER_IDS_WITH_CHILDREN_PARAMETER.includes(careerId) && <Children />}

          <FormField
            fieldLabel={`Что бы вы хотели чтобы делала ${careerDictionary[careerId].translation.nameSingle}`}
            name="translations.0.description"
            sx={{ width: '100%' }}
            type="textArea"
          />

          {CAREER_IDS_WITH_SICK_CHILD_PARAMETER.includes(careerId) && (
            <FormField label="Ребенку нездоровится" name="sickChild" type="checkboxChecker" />
          )}

          {CAREER_IDS_WITH_CHILD_WITH_DISABILITIES_PARAMETER.includes(careerId) && (
            <FormField label="У меня есть особый ребенок" name="childWithDisabilities" type="checkboxChecker" />
          )}

          <FormField
            label={`Ищу постоянную ${careerDictionary[careerId].translation.nameSingleAccusative}`}
            name="regularSpecialist"
            type="checkboxChecker"
          />

          {CAREER_IDS_WITH_AFTER_YH_SCHOOL_PARAMETER.includes(careerId) && (
            <FormField label="Только после школы YouHelp" name="trainingCompleted" type="checkboxChecker" />
          )}

          {CAREER_IDS_WITH_AUTO_SELECTION_PARAMETER.includes(careerId) && <AutoSelection />}

          {buttonsInsideFormContainer && <Box sx={{ mt: 2.5 }}>{buttons}</Box>}
        </Stack>
      </Wrapper>
      {buttonsInsideFormContainer ? null : (
        <ModalViewButtons>
          <Wrapper sx={wrapperSx}>{buttons}</Wrapper>
        </ModalViewButtons>
      )}
    </Stack>
  );
};
